/**
 * CustomFaixaVerComo — a tarja que aparece quando o admin está enxergando o app
 * pelos olhos de outra pessoa.
 *
 * Fica FIXA no rodapé da janela e é impossível de não ver, de propósito: o
 * risco desse modo não é técnico (o servidor recusa toda escrita), é humano —
 * esquecer que se está na sessão de outro e concluir que "o app está errado"
 * por não ver o que deveria ver.
 *
 * No rodapé e não no topo porque o topo já tem o header fixo do tema, e
 * empurrá-lo mudaria a conta de `paddingTop` que o InternalLayout faz.
 *
 * Portado do projeto irmão `webapp-rm-ops` (09/08/2026).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';

export default function CustomFaixaVerComo() {
  const { usuario, voltarAoMeuAcesso } = useAuth();
  const [voltando, setVoltando] = useState(false);
  // No celular a barra de atalhos também mora no rodapé, e a tarja a cobria
  // inteira — o admin via o app "como" a pessoa mas sem a navegação dela, que é
  // justamente o que ele foi conferir. A tarja sobe a altura da barra; no
  // desktop a barra é `d-lg-none`, mede 0, e a tarja volta ao rodapé.
  const [acimaDe, setAcimaDe] = useState(0);

  useEffect(() => {
    const medir = () => {
      const barra = document.querySelector<HTMLElement>('.adminuiux-mobile-footer');
      setAcimaDe(barra?.offsetHeight ?? 0);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [usuario?.vendo_como]);

  if (!usuario?.vendo_como) return null;

  const voltar = async () => {
    setVoltando(true);
    try {
      await voltarAoMeuAcesso();
    } catch {
      toast.error('Não foi possível voltar. Saia e entre de novo.');
      setVoltando(false);
    }
  };

  return (
    <div
      className="position-fixed start-0 end-0 bg-warning text-dark shadow-lg"
      style={{
        bottom: acimaDe,
        zIndex: 1080,
        paddingBottom: acimaDe ? undefined : 'env(safe-area-inset-bottom)',
      }}
      role="status"
    >
      <div className="container-fluid py-2">
        <div className="row gx-2 align-items-center">
          <div className="col-auto">
            <i className="bi bi-eye-fill"></i>
          </div>
          <div className="col small lh-sm">
            Você está vendo o app como <strong>{usuario.nome}</strong>
            {usuario.perfil_nome && ` (${usuario.perfil_nome})`}.
            <span className="d-none d-sm-inline"> Nada é gravado neste modo.</span>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-sm btn-dark"
              type="button"
              onClick={voltar}
              disabled={voltando}
            >
              {voltando ? 'Voltando…' : 'Voltar ao meu acesso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
